// frontend/src/components/Events/EventParticipateModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  FormHelperText,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  alpha,
  useTheme,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Mic as MicIcon,
  Headset as HeadsetIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Upload as UploadIcon,
  Check as CheckIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Article as ArticleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { projectsAPI } from '../../services/api';
import { participantsAPI } from '../../services/eventsAPI';
import { useAuth } from '../../contexts/AuthContext';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ — iOS / Glass
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': { padding: theme.spacing(2) },
  '& .MuiDialog-paper': {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    minWidth: 0,
    maxHeight: 'min(900px, calc(100vh - 32px))',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.76)}, ${alpha(theme.palette.grey[100], 0.48)} 52%, ${alpha(theme.palette.primary.main, 0.075)}), radial-gradient(circle at 92% 92%, ${alpha(theme.palette.primary.main, 0.12)}, transparent 38%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.58),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.6),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.52)}, ${alpha(theme.palette.common.white, 0.16)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2.4, 2.6),
  background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.14)}, ${alpha(theme.palette.primary.main, 0.025)})`,
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: alpha(theme.palette.common.white, 0.28), borderRadius: '999px' },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.22),
    borderRadius: '999px',
    '&:hover': { background: alpha(theme.palette.primary.main, 0.45) },
  },
}));

const GlassSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.8),
  borderRadius: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.44)}, ${alpha(theme.palette.common.white, 0.16)}), radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.055)}, transparent 45%)`,
  backgroundColor: alpha(theme.palette.common.white, 0.28),
  backdropFilter: 'blur(16px) saturate(130%)',
  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
  boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.045)}`,
}));

const TypeCard = styled(Paper, { shouldForwardProp: (prop) => prop !== 'selected' })(({ theme, selected }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(3),
  border: `1px solid ${selected ? alpha(theme.palette.primary.main, 0.48) : alpha(theme.palette.common.white, 0.72)}`,
  background: selected
    ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.11)}, ${alpha(theme.palette.common.white, 0.34)})`
    : `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.20)})`,
  backdropFilter: 'blur(14px) saturate(130%)',
  WebkitBackdropFilter: 'blur(14px) saturate(130%)',
  cursor: 'pointer',
  transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
  boxShadow: selected ? `0 10px 28px ${alpha(theme.palette.primary.main, 0.13)}` : `0 8px 24px ${alpha(theme.palette.common.black, 0.045)}`,
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.primary.main, 0.52),
    boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.11)}`,
  },
}));

const ProjectCard = styled(Paper, { shouldForwardProp: (prop) => prop !== 'selected' })(({ theme, selected }) => ({
  padding: theme.spacing(1.35, 1.5),
  borderRadius: theme.spacing(2.6),
  border: `1px solid ${selected ? alpha(theme.palette.primary.main, 0.44) : alpha(theme.palette.common.white, 0.68)}`,
  background: selected
    ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.09)}, ${alpha(theme.palette.common.white, 0.30)})`
    : `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.42)}, ${alpha(theme.palette.common.white, 0.16)})`,
  backdropFilter: 'blur(12px) saturate(125%)',
  WebkitBackdropFilter: 'blur(12px) saturate(125%)',
  cursor: 'pointer',
  transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
  marginBottom: theme.spacing(1),
  boxShadow: selected ? `0 7px 22px ${alpha(theme.palette.primary.main, 0.10)}` : `0 5px 18px ${alpha(theme.palette.common.black, 0.035)}`,
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.primary.main, 0.42),
  },
}));

const DropzoneArea = styled(Paper, { shouldForwardProp: (prop) => prop !== 'isdragactive' })(({ theme, isdragactive }) => ({
  padding: theme.spacing(2.6),
  textAlign: 'center',
  cursor: 'pointer',
  width: '100%',
  borderRadius: theme.spacing(3),
  border: `1.5px dashed ${isdragactive === 'true' ? alpha(theme.palette.primary.main, 0.72) : alpha(theme.palette.common.white, 0.82)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.18)}), radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 48%)`,
  backgroundColor: alpha(theme.palette.common.white, 0.32),
  backdropFilter: 'blur(18px) saturate(135%)',
  WebkitBackdropFilter: 'blur(18px) saturate(135%)',
  boxShadow: isdragactive === 'true' ? `0 12px 34px ${alpha(theme.palette.primary.main, 0.15)}` : `0 10px 28px ${alpha(theme.palette.common.black, 0.045)}`,
  transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.54),
    boxShadow: `0 14px 34px ${alpha(theme.palette.primary.main, 0.10)}`,
    transform: 'translateY(-1px)',
  },
}));

const SourceToggle = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(0.7),
  padding: theme.spacing(0.55),
  marginBottom: theme.spacing(2.2),
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.34),
  border: `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
  '& .MuiButton-root': {
    minHeight: 42,
    border: 0,
    borderRadius: 999,
    padding: theme.spacing(1, 1.5),
    textTransform: 'none',
    fontWeight: 650,
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&.MuiButton-contained': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      boxShadow: `0 5px 16px ${alpha(theme.palette.primary.main, 0.10)}`,
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.07),
    },
  },
}));

const EventTitle = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.2),
  padding: theme.spacing(1.5, 1.7),
  borderRadius: theme.spacing(2.6),
  border: `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.18)})`,
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.04)}`,
  '& .MuiTypography-root': { fontWeight: 700 },
}));

const GlassButton = styled(Button)(({ theme }) => ({
  minHeight: 42,
  borderRadius: 999,
  textTransform: 'none',
  paddingLeft: theme.spacing(2.3),
  paddingRight: theme.spacing(2.3),
  fontWeight: 650,
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.common.white, 0.48),
  border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.055)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.72),
    borderColor: alpha(theme.palette.common.white, 0.96),
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  minHeight: 42,
  borderRadius: 999,
  textTransform: 'none',
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  fontWeight: 700,
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.96)}, ${alpha(theme.palette.primary.dark, 0.96)})`,
  boxShadow: `0 9px 26px ${alpha(theme.palette.primary.main, 0.22)}`,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.30)}`,
    transform: 'translateY(-1px)',
  },
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EventParticipateModal = ({
  open,
  onClose,
  eventId,
  eventTitle,
  eventType,  // 🔥 ДОБАВЛЯЕМ ПРОПС (раньше его не было!)
  sections = [],
  existingParticipant = null,
  mode = 'create',
  onSuccess
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Определяем, конкурс ли это
  const isCompetition = eventType === 'competition';

  // Состояния формы
  const [participationType, setParticipationType] = useState(isCompetition ? 'listener' : 'listener');
  const [selectedSection, setSelectedSection] = useState(null);
  const [isPlenary, setIsPlenary] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sourceType, setSourceType] = useState('files');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [talkTitle, setTalkTitle] = useState('');

  // Данные
  const [completedProjects, setCompletedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Определяем, можно ли выбирать роль слушателя (заблокировано для докладчиков)
  const isSpeakerInEditMode = mode === 'edit' && existingParticipant?.participation_type === 'speaker';

  // Загрузка завершенных проектов при открытии
  useEffect(() => {
    if (open && user) {
      loadCompletedProjects();
    }
  }, [open, user]);

  // Загрузка данных существующего участия (для режима редактирования)
  useEffect(() => {
    if (open && mode === 'edit' && existingParticipant) {
      setParticipationType(existingParticipant.participation_type);
      setTalkTitle(existingParticipant.talk_title || '');
      setIsPlenary(
        existingParticipant.participation_type === 'speaker'
          ? Boolean(existingParticipant.is_plenary)
          : false
      );

      if (existingParticipant.section) {
        setSelectedSection(existingParticipant.section);
      }

      if (existingParticipant.project) {
        setSourceType('project');
        setSelectedProject(existingParticipant.project);
      } else if (existingParticipant.uploaded_files?.length > 0) {
        setSourceType('files');
        setUploadedFiles(existingParticipant.uploaded_files);
      }
    }
  }, [open, mode, existingParticipant]);

  // Сбрасываем ошибку при изменении формы
  useEffect(() => {
    if (error) {
      setError('');
    }
  }, [participationType, sourceType, selectedSection, isPlenary, selectedProject, uploadedFiles, talkTitle]);

  const loadCompletedProjects = async () => {
    try {
      const projectsResponse = await projectsAPI.getAll({
        status: 'completed'
      });

      const projects = Array.isArray(projectsResponse)
        ? projectsResponse
        : (projectsResponse?.results || []);

      const validProjects = projects.filter(p => p !== null && p !== undefined);

      const userProjects = validProjects.filter(p =>
        p.owner?.id === user?.id ||
        p.members?.some(m => m.id === user?.id)
      );

      setCompletedProjects(userProjects);
    } catch (error) {
      console.error('Ошибка загрузки проектов:', error);
    }
  };

  const handleTypeSelect = (type) => {
    if (isSpeakerInEditMode && type === 'listener') return;
    setParticipationType(type);
    if (type === 'listener') {
      setSelectedSection(null);
      setIsPlenary(false);
      setSelectedProject(null);
      setSourceType('files');
      setUploadedFiles([]);
      setTalkTitle('');
    }
    // 🔥 Если выбрал докладчика и нет секций — авто-пленарное
    if (type === 'speaker' && sections.length === 0) {
      setSelectedSection(null);
      setIsPlenary(true);
    }
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setIsPlenary(false);
  };

  const handlePlenarySelect = () => {
    setSelectedSection(null);
    setIsPlenary(true);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const handleSourceChange = (newSource) => {
    setSourceType(newSource);
    if (newSource === 'project') {
      setUploadedFiles([]);
    } else {
      setSelectedProject(null);
    }
  };

  const onDrop = async (acceptedFiles) => {
    setUploading(true);
    setError('');

    try {
      const newFiles = [];

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];

        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const response = await participantsAPI.uploadParticipationFile(eventId, file, (progress) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        });

        if (response) {
          newFiles.push({
            id: response.id || Date.now() + i,
            name: file.name,
            size: file.size,
            type: file.type,
            url: response.url,
            uploaded_at: new Date().toISOString()
          });
        }

        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }

      setUploadedFiles(prev => [...prev, ...newFiles]);
    } catch (err) {
      console.error('Ошибка загрузки файлов:', err);
      setError('Не удалось загрузить файлы');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploading,
    maxSize: 10 * 1024 * 1024,
  });

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleCancelParticipation = async () => {
    if (!window.confirm('Вы уверены, что хотите отказаться от участия?')) return;

    setLoading(true);
    try {
      await participantsAPI.cancelParticipation(existingParticipant.id);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      console.error('Ошибка при отказе от участия:', err);
      setError('Не удалось отказаться от участия');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!participationType) {
      setError('Выберите тип участия');
      return false;
    }

    if (participationType === 'speaker') {
      if (!isPlenary && sections.length > 0 && !selectedSection) {
        setError('Выберите секцию или пленарное заседание');
        return false;
      }

      if (!talkTitle.trim()) {
        setError(isCompetition ? 'Укажите название работы' : 'Укажите тему доклада');
        return false;
      }

      if (sourceType === 'project' && !selectedProject) {
        setError('Выберите проект');
        return false;
      }

      if (sourceType === 'files' && uploadedFiles.length === 0) {
        setError('Загрузите файлы материалов');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (!eventId) {
      setError('Ошибка: ID мероприятия не найден');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = {
        participation_type: participationType,
        section_id: selectedSection?.id || null,
        is_plenary: participationType === 'speaker' ? isPlenary : false,
        project_id: selectedProject?.id || null,
        uploaded_files: sourceType === 'files' ? uploadedFiles : [],
        talk_title: participationType === 'speaker' ? talkTitle : ''
      };

      console.log('📦 ОТПРАВКА ДАННЫХ НА СЕРВЕР:', {
        ...data,
        uploaded_files: data.uploaded_files.length
      });

      let response;
      if (mode === 'create') {
        response = await participantsAPI.participate(eventId, data);
      } else {
        response = await participantsAPI.updateParticipant(existingParticipant.id, data);
      }

      console.log('✅ ОТВЕТ ОТ СЕРВЕРА:', response);

      setSuccess(true);

      if (mode === 'edit' && response.status === 'pending') {
        alert('Ваша заявка отправлена на повторное рассмотрение организатору');
      }

      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess();
      }, 1500);

    } catch (err) {
      console.error('❌ ОШИБКА ПРИ ОТПРАВКЕ:', err);

      let errorMessage = 'Не удалось отправить заявку';

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data && typeof err.response.data === 'object') {
        const errorMessages = Object.values(err.response.data).flat();
        errorMessage = errorMessages.join('; ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth={false}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight="700" sx={{ color: 'primary.main', whiteSpace: 'normal', lineHeight: 1.2 }}>
              {mode === 'create'
                ? (isCompetition ? 'Подача заявки на конкурс' : 'Подача заявки')
                : (isCompetition ? 'Управление участием в конкурсе' : 'Управление участием')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ width: 40, height: 40, color: 'text.secondary', backgroundColor: alpha(theme.palette.common.white, 0.44), border: `1px solid ${alpha(theme.palette.common.white, 0.80)}`, '&:hover': { color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.09) } }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Заголовок мероприятия */}
        <EventTitle sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.45 }}>
            Мероприятие
          </Typography>
          <Typography variant="h5" sx={{ color: 'primary.main', lineHeight: 1.2 }}>
            {eventTitle || 'Мероприятие'}
          </Typography>
        </EventTitle>

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: theme.spacing(2) }}>
            {mode === 'create' ? 'Заявка успешно отправлена!' : 'Изменения сохранены!'}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        <GlassSection sx={{ mb: 2.2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1.2, color: 'text.secondary', fontWeight: 650, textTransform: 'uppercase', letterSpacing: 0.45 }}>
            Тип участия
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.2 }}>
          <TypeCard
            selected={participationType === 'listener'}
            onClick={() => handleTypeSelect('listener')}
            sx={{
              flex: 1,
              opacity: isSpeakerInEditMode ? 0.5 : 1,
              cursor: isSpeakerInEditMode ? 'not-allowed' : 'pointer'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HeadsetIcon color={participationType === 'listener' ? 'primary' : 'inherit'} />
              <Typography variant="subtitle1" fontWeight="600">
                {isCompetition ? 'Участник' : 'Слушатель'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {isCompetition
                ? 'Участие в конкурсе без представления работы'
                : 'Посещение мероприятия без доклада'}
            </Typography>
            {isSpeakerInEditMode && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                Недоступно
              </Typography>
            )}
          </TypeCard>

          <TypeCard
            selected={participationType === 'speaker'}
            onClick={() => handleTypeSelect('speaker')}
            sx={{ flex: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MicIcon color={participationType === 'speaker' ? 'primary' : 'inherit'} />
              <Typography variant="subtitle1" fontWeight="600">
                {isCompetition ? 'Конкурсант' : 'Докладчик'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {isCompetition
                ? 'Представление конкурсной работы'
                : 'Презентация своего проекта или доклада'}
            </Typography>
          </TypeCard>
          </Box>
        </GlassSection>

        {participationType === 'speaker' && (
          <>
            <Divider sx={{ my: 3 }} />

            <GlassSection sx={{ mb: 2.2 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.1, color: 'text.secondary', fontWeight: 650, textTransform: 'uppercase', letterSpacing: 0.45 }}>
                {isCompetition ? 'Детали конкурсной работы' : 'Детали выступления'}
              </Typography>

            <TextField
              fullWidth
              label={isCompetition ? 'Название работы *' : 'Тема доклада *'}
              value={talkTitle}
              onChange={(e) => setTalkTitle(e.target.value)}
              placeholder={isCompetition
                ? "Например: 'Исследование влияния цифровизации на экономику'"
                : "Например: 'Влияние искусственного интеллекта на современную науку'"
              }
              multiline
              rows={2}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />

            <Typography variant="subtitle2" fontWeight="500" gutterBottom sx={{ mt: 2 }}>
              {isCompetition ? 'Секция конкурса *' : 'Место выступления *'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mb: 3 }}>
              {/* Пленарное заседание / Основной конкурс */}
              <ProjectCard
                selected={selectedSection === null && isPlenary}
                onClick={handlePlenarySelect}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                    🎤
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="600">
                      {isCompetition ? 'Основной конкурс' : 'Пленарное заседание'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isCompetition
                        ? 'Участие в основном конкурсе'
                        : 'Выступление на главной сцене'}
                    </Typography>
                  </Box>
                  {selectedSection === null && isPlenary && (
                    <CheckCircleIcon color="primary" />
                  )}
                </Box>
              </ProjectCard>

              {/* Секции */}
              {sections.map((section) => (
                <ProjectCard
                  key={section.id}
                  selected={selectedSection?.id === section.id}
                  onClick={() => handleSectionSelect(section)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: section.color || theme.palette.primary.main, width: 32, height: 32 }}>
                      {section.title[0]}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="600">
                        {section.title}
                      </Typography>
                      {section.description && (
                        <Typography variant="caption" color="text.secondary">
                          {section.description}
                        </Typography>
                      )}
                    </Box>
                    {selectedSection?.id === section.id && (
                      <CheckCircleIcon color="primary" />
                    )}
                  </Box>
                </ProjectCard>
              ))}
            </Box>

            {completedProjects.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                  Источник материалов
                </Typography>
                <SourceToggle>
                  <Button
                    variant={sourceType === 'project' ? 'contained' : 'outlined'}
                    onClick={() => handleSourceChange('project')}
                    startIcon={<FolderIcon />}
                  >
                    Использовать проект
                  </Button>
                  <Button
                    variant={sourceType === 'files' ? 'contained' : 'outlined'}
                    onClick={() => handleSourceChange('files')}
                    startIcon={<UploadIcon />}
                  >
                    Загрузить файлы
                  </Button>
                </SourceToggle>
              </>
            )}

            {sourceType === 'project' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                  Выберите завершенный проект *
                </Typography>
                {completedProjects.length > 0 ? (
                  completedProjects.map((project) => {
                    if (!project || !project.id) return null;

                    const projectTitle = project.title || 'Без названия';
                    const memberCount = project.member_count || 0;

                    return (
                      <ProjectCard
                        key={project.id}
                        selected={selectedProject?.id === project.id}
                        onClick={() => handleProjectSelect(project)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: theme.palette.success.main, width: 32, height: 32 }}>
                            <CheckIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="600">
                              {projectTitle}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Завершен • {memberCount} участников
                            </Typography>
                          </Box>
                          {selectedProject?.id === project.id && (
                            <CheckCircleIcon color="primary" />
                          )}
                        </Box>
                      </ProjectCard>
                    );
                  })
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    У вас нет завершенных проектов
                  </Alert>
                )}
              </Box>
            )}

            {sourceType === 'files' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                  Загрузите материалы *
                </Typography>

                <DropzoneArea
                  {...getRootProps()}
                  isdragactive={isDragActive ? 'true' : 'false'}
                >
                  <input {...getInputProps()} />
                  <UploadIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    {isDragActive ? 'Отпустите файлы' : 'Перетащите файлы'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    или кликните для выбора (макс. 10MB)
                  </Typography>
                </DropzoneArea>

                {Object.keys(uploadProgress).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(uploadProgress).map(([fileName, progress]) => (
                      <Box key={fileName} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 250 }}>
                            {fileName}
                          </Typography>
                          <Typography variant="caption">{progress}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {uploadedFiles.length > 0 && (
                  <List sx={{ mt: 2 }}>
                    {uploadedFiles.map((file) => {
                      if (!file) return null;

                      const fileName = file.name || 'Файл';
                      const fileSize = file.size || 0;

                      return (
                        <ListItem
                          key={file.id}
                          sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                            mb: 1,
                            border: '0.5px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <ListItemIcon>
                            <DescriptionIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={fileName}
                            secondary={formatFileSize(fileSize)}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveFile(file.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}
            </GlassSection>
          </>
        )}

        {participationType === 'listener' && (
          <GlassSection sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 40, height: 40, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.10), border: `1px solid ${alpha(theme.palette.common.white, 0.76)}` }}>
                <HeadsetIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" gutterBottom>
                  {isCompetition
                    ? 'Вы выбрали участие в конкурсе без представления работы.'
                    : 'Вы выбрали участие в качестве слушателя.'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isCompetition
                    ? 'После подтверждения заявки вы сможете следить за ходом конкурса.'
                    : 'После подтверждения заявки вы сможете посещать все секции мероприятия.'}
                </Typography>
              </Box>
            </Box>
          </GlassSection>
        )}
      </StyledDialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {mode === 'edit' && (
            <Button
              onClick={handleCancelParticipation}
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              sx={{ borderRadius: 999, px: 2.3, backgroundColor: alpha(theme.palette.error.main, 0.06), borderColor: alpha(theme.palette.error.main, 0.20) }}
            >
              Отказаться
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GlassButton onClick={onClose} startIcon={<CloseIcon />}>
            Отмена
          </GlassButton>
          <PrimaryButton
            onClick={handleSubmit}

            disabled={loading || (participationType === 'speaker' && sourceType === 'files' && uploading)}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading
              ? 'Отправка...'
              : mode === 'create'
                ? (isCompetition ? 'Отправить заявку на конкурс' : 'Отправить заявку')
                : 'Сохранить изменения'}
          </PrimaryButton>
        </Box>
      </DialogActions>
    </StyledDialog>
  );
};

export default EventParticipateModal;